/**
 * BusinessMentionCard Component
 *
 * Expandable card for displaying and reviewing mentions on the Business details page
 * Features single expansion, inline approve/reject forms, and full mention details
 */
import React, { useState, useRef, useEffect } from 'react';
import { Card, Stack, Group, Text, Badge, Button, Collapse, Box, Divider, Checkbox } from '@mantine/core';
import { IconCheck, IconX, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import type { MentionReviewDto } from '@/domain/entities/MentionReview';
import { getConfidenceLevel } from '@/presentation/components/admin/MentionReview/MentionReviewSection.types';
import { getReviewStatusLabel, getReviewStatusColor } from '@/domain/entities/ReviewStatus';
import { formatRedditDate } from '@/presentation/components/domain/MentionReview';
import {
  ConfidenceBreakdownCard,
  MentionContentCard,
  MentionRedditContextCard,
  MentionMatchedBusinessCard,
  MentionApproveForm,
  MentionRejectForm
} from '@/presentation/components/domain/MentionReview';

export interface BusinessMentionCardProps {
  /**
   * Mention data
   */
  mention: MentionReviewDto;

  /**
   * Whether this card is expanded
   */
  isExpanded: boolean;

  /**
   * Callback when card expand/collapse is toggled
   */
  onToggleExpand: (mentionId: string) => void;

  /**
   * Callback when approve is confirmed (optional - not provided for inactive mentions)
   */
  onApprove?: (mentionId: string, adminNotes?: string) => Promise<void>;

  /**
   * Callback when reject is confirmed (optional - not provided for inactive mentions)
   */
  onReject?: (mentionId: string, rejectionReason: string, adminNotes?: string) => Promise<void>;

  /**
   * Whether actions are disabled (mutations in progress)
   */
  actionsDisabled: boolean;

  /**
   * Sprint 066: Render a bulk-selection checkbox in the collapsed header
   */
  showSelection?: boolean;

  /**
   * Sprint 066: Whether this card is currently selected for bulk reassignment
   */
  isSelected?: boolean;

  /**
   * Sprint 066: Callback fired when the selection checkbox is toggled
   */
  onSelectionChange?: (mentionId: string, selected: boolean) => void;
}

/**
 * BusinessMentionCard - Expandable card with inline review
 */
export const BusinessMentionCard: React.FC<BusinessMentionCardProps> = ({
  mention,
  isExpanded,
  onToggleExpand,
  onApprove,
  onReject,
  actionsDisabled,
  showSelection = false,
  isSelected = false,
  onSelectionChange
}): React.ReactElement => {
  // Refs for scrolling
  const cardRef = useRef<HTMLDivElement>(null);
  const actionFormRef = useRef<HTMLDivElement>(null);

  // Form state
  const [adminNotes, setAdminNotes] = useState<string>('');
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [showApproveForm, setShowApproveForm] = useState<boolean>(false);
  const [showRejectForm, setShowRejectForm] = useState<boolean>(false);
  const [isPending, setIsPending] = useState<boolean>(false);

  // Calculate display values
  const confidenceScore = mention.confidenceBreakdown?.finalScore ?? mention.matchConfidenceScore;
  const confidenceLevel = getConfidenceLevel(confidenceScore);
  const statusColor = getReviewStatusColor(
    mention.reviewStatus as 'Pending' | 'Approved' | 'Rejected' | 'Flagged' | ''
  );
  const statusLabel = getReviewStatusLabel(
    mention.reviewStatus as 'Pending' | 'Approved' | 'Rejected' | 'Flagged' | ''
  );
  const isPendingReview = mention.reviewStatus === 'Pending';

  // Truncate content for collapsed view
  const truncatedContent =
    mention.content.length > 100 ? `${mention.content.substring(0, 100)}...` : mention.content;

  // Scroll to action form when approve/reject is clicked
  useEffect(() => {
    if ((showApproveForm || showRejectForm) && actionFormRef.current) {
      setTimeout(() => {
        actionFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100); // Small delay to allow Collapse animation to start
    }
  }, [showApproveForm, showRejectForm]);

  // Handlers
  const handleToggleExpand = (): void => {
    onToggleExpand(mention.id);
    // Reset forms when collapsing
    if (isExpanded) {
      setShowApproveForm(false);
      setShowRejectForm(false);
      setAdminNotes('');
      setRejectionReason('');
    }
  };

  const handleApproveClick = (): void => {
    setShowApproveForm(true);
    setShowRejectForm(false);
  };

  const handleRejectClick = (): void => {
    setShowRejectForm(true);
    setShowApproveForm(false);
  };

  const handleApproveConfirm = async (): Promise<void> => {
    if (!onApprove) return;

    setIsPending(true);
    try {
      await onApprove(mention.id, adminNotes || undefined);
      // Reset form
      setShowApproveForm(false);
      setAdminNotes('');
      // Scroll back to top of card after review
      setTimeout(() => {
        cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } finally {
      setIsPending(false);
    }
  };

  const handleRejectConfirm = async (): Promise<void> => {
    if (!rejectionReason || !onReject) return;
    setIsPending(true);
    try {
      await onReject(mention.id, rejectionReason, adminNotes || undefined);
      // Reset form
      setShowRejectForm(false);
      setAdminNotes('');
      setRejectionReason('');
      // Scroll back to top of card after review
      setTimeout(() => {
        cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } finally {
      setIsPending(false);
    }
  };

  const handleCancel = (): void => {
    setAdminNotes('');
    setRejectionReason('');
    setShowApproveForm(false);
    setShowRejectForm(false);
  };

  return (
    <Card withBorder padding="md" shadow="sm" ref={cardRef}>
      <Stack gap="md">
        {/* Collapsed Header - Always Visible */}
        <Box
          onClick={handleToggleExpand}
          style={{ cursor: 'pointer', userSelect: 'none' }}
          data-testid="mention-card-header"
        >
          <Group justify="space-between" wrap="wrap">
            <Group gap="xs" wrap="wrap">
              {/* Sprint 066: Bulk selection checkbox */}
              {showSelection && (
                <Checkbox
                  checked={isSelected}
                  onChange={(e): void => {
                    e.stopPropagation();
                    onSelectionChange?.(mention.id, e.currentTarget.checked);
                  }}
                  onClick={(e): void => e.stopPropagation()}
                  aria-label={`Select mention ${mention.id}`}
                  disabled={actionsDisabled}
                />
              )}

              {/* Confidence Badge */}
              <Badge color={confidenceLevel.color} variant="filled" size="md">
                {confidenceScore}% {confidenceLevel.label}
              </Badge>

              {/* Status Badge */}
              <Badge color={statusColor} variant="light" size="md">
                {statusLabel}
              </Badge>

              {/* Company/Franchise Indicator */}
              {mention.isCompanyMention && (
                <Badge color="blue" variant="outline" size="md">
                  Company/Franchise
                </Badge>
              )}
            </Group>

            <Group gap="sm">
              {/* Quick Action Buttons - Always Visible */}
              {isPendingReview && !isExpanded && (
                <>
                  <Button
                    size="xs"
                    color="green"
                    variant="light"
                    leftSection={<IconCheck size={14} />}
                    onClick={(e): void => {
                      e.stopPropagation();
                      onToggleExpand(mention.id);
                      setShowApproveForm(true);
                    }}
                    disabled={actionsDisabled}
                  >
                    Approve
                  </Button>
                  <Button
                    size="xs"
                    color="red"
                    variant="light"
                    leftSection={<IconX size={14} />}
                    onClick={(e): void => {
                      e.stopPropagation();
                      onToggleExpand(mention.id);
                      setShowRejectForm(true);
                    }}
                    disabled={actionsDisabled}
                  >
                    Reject
                  </Button>
                </>
              )}

              {/* Expand/Collapse Icon */}
              {isExpanded ? <IconChevronUp size={20} /> : <IconChevronDown size={20} />}
            </Group>
          </Group>

          {/* Preview Info */}
          <Group mt="sm" gap="md" wrap="wrap">
            <Text size="sm" c="dimmed">
              {formatRedditDate(mention.postCreatedAt)}
            </Text>
            <Text size="sm" c="dimmed">
              r/{mention.subredditName}
            </Text>
          </Group>

          {/* Truncated Content */}
          <Text size="sm" mt="xs" lineClamp={2}>
            {truncatedContent}
          </Text>
        </Box>

        {/* Expanded Content */}
        <Collapse in={isExpanded}>
          <Stack gap="md">
            <Divider />

            {/* Full Confidence Breakdown */}
            <ConfidenceBreakdownCard
              confidenceBreakdown={mention.confidenceBreakdown}
              matchConfidenceScore={mention.matchConfidenceScore}
            />

            {/* Full Mention Content */}
            <MentionContentCard content={mention.content} />

            {/* Matched Business Details */}
            <MentionMatchedBusinessCard
              businessId={mention.businessId}
              businessName={mention.businessName}
              businessAddress={mention.businessAddress}
              businessPhone={mention.businessPhone}
            />

            {/* Reddit Context */}
            <MentionRedditContextCard
              postCreatedAt={mention.postCreatedAt}
              redditAuthor={mention.redditAuthor}
              subredditName={mention.subredditName}
              redditPermalink={mention.redditPermalink}
              redditPostId={mention.redditPostId}
            />

            {/* Review History (if already reviewed) */}
            {!isPendingReview && mention.reviewedAt && (
              <Card withBorder padding="md" shadow="sm">
                <Stack gap="xs">
                  <Text size="sm" fw={500}>
                    Review History
                  </Text>
                  <Group gap="xs">
                    <Text size="xs" c="dimmed">
                      Reviewed: {formatRedditDate(mention.reviewedAt)}
                    </Text>
                  </Group>
                  {mention.adminNotes && (
                    <Box>
                      <Text size="xs" fw={500} mb="xs">
                        Admin Notes:
                      </Text>
                      <Text size="xs" c="dimmed" fs="italic">
                        {mention.adminNotes}
                      </Text>
                    </Box>
                  )}
                </Stack>
              </Card>
            )}

            {/* Action Buttons (only for pending mentions) */}
            {isPendingReview && (
              <Card withBorder padding="md" shadow="sm" ref={actionFormRef}>
                {!showApproveForm && !showRejectForm ? (
                  <Group gap="md">
                    <Button
                      color="green"
                      leftSection={<IconCheck size={16} />}
                      onClick={handleApproveClick}
                      disabled={actionsDisabled || isPending}
                    >
                      Approve Mention
                    </Button>
                    <Button
                      color="red"
                      variant="outline"
                      leftSection={<IconX size={16} />}
                      onClick={handleRejectClick}
                      disabled={actionsDisabled || isPending}
                    >
                      Reject Mention
                    </Button>
                  </Group>
                ) : null}

                {/* Approve Form */}
                {showApproveForm && (
                  <MentionApproveForm
                    adminNotes={adminNotes}
                    onAdminNotesChange={setAdminNotes}
                    onConfirm={handleApproveConfirm}
                    onCancel={handleCancel}
                    disabled={actionsDisabled}
                    isPending={isPending}
                  />
                )}

                {/* Reject Form */}
                {showRejectForm && (
                  <MentionRejectForm
                    rejectionReason={rejectionReason}
                    onRejectionReasonChange={setRejectionReason}
                    adminNotes={adminNotes}
                    onAdminNotesChange={setAdminNotes}
                    onConfirm={handleRejectConfirm}
                    onCancel={handleCancel}
                    disabled={actionsDisabled}
                    isPending={isPending}
                  />
                )}
              </Card>
            )}
          </Stack>
        </Collapse>
      </Stack>
    </Card>
  );
};
